import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Eye, MousePointerClick, TrendingUp } from 'lucide-react';
import type { UnitInterestMetrics } from '@/lib/admin/analytics';
import { useNavigate } from 'react-router-dom';

interface UnitInterestRankingProps {
  data: UnitInterestMetrics[];
  onUnitClick?: (unitId: string) => void;
}

export function UnitInterestRanking({ data, onUnitClick }: UnitInterestRankingProps) {
  const navigate = useNavigate();
  const topUnits = data.slice(0, 10);

  const handleUnitClick = (unitId: string) => {
    if (onUnitClick) {
      onUnitClick(unitId);
    } else {
      navigate(`/residences/${unitId}`);
    }
  };

  if (topUnits.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top Units by Interest</CardTitle>
          <CardDescription>Most viewed and engaged units</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No interest data available yet
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Units by Interest</CardTitle>
        <CardDescription>Most viewed and engaged units</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {topUnits.map((unit, index) => (
            <div
              key={unit.unitId}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{unit.unitNumber}</div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      <span>{unit.viewCount} views</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MousePointerClick className="h-3 w-3" />
                      <span>{unit.clickCount} clicks</span>
                    </div>
                    {unit.formSubmissions > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {unit.formSubmissions} form{unit.formSubmissions > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="flex items-center gap-1 text-sm font-semibold">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    {unit.engagementScore}
                  </div>
                  <div className="text-xs text-muted-foreground">Engagement</div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUnitClick(unit.unitId)}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}







